import type { Distinction, Reference, ResearchEntry } from '@/types/content'

/** Thesis work, most recent first. */
export const research: readonly ResearchEntry[] = [
  {
    id: 'meng-checkme',
    level: 'Level 500 — MEng Thesis',
    title: 'CheckMe SaaS Platform: UI/UX Design and Cloud Deployment',
    period: '2023 - 2024',
    grade: 'Excellent',
    supervisors: [
      'Pr. Amougou Ngoumou',
      'Dr. Bekono Boris Davy',
      'MSc. Nguensie Wakponou Bernice',
    ],
    highlights: [
      'Engineered a dual deployment architecture supporting cloud operation on AWS EC2 and on-premise deployment within a VMware ESXi university data centre.',
      'Configured full containerisation via Docker, CI/CD pipelines and end-to-end TLS security.',
      'Implemented multi-modal biometric authentication (fingerprint and QR code), JWT session management, and a six-level role-based access control hierarchy supporting 10,000+ concurrent users.',
    ],
  },
  {
    id: 'l400-tvbox',
    level: 'Level 400 Thesis',
    title: 'TV Box Hardware Architecture Design',
    period: '2022 - 2023',
    grade: 'Excellent',
    supervisors: [
      'Pr. Videme Bossou Olivier',
      'Pr. Atangana Jacques',
      'MSc. Nguensie Bernice',
    ],
    highlights: [
      'Designed the complete hardware architecture of an Android TV set-top box for the African consumer market.',
      'Covered SoC selection, motherboard layout and embedded systems co-design.',
    ],
  },
  {
    id: 'l300-gsm',
    level: 'Level 300 Thesis',
    title: 'GSM/GPRS Integration into Android TV: Internet Connectivity via SIM Card',
    period: '2021 - 2022',
    grade: 'Excellent',
    supervisors: [
      'Pr. Nlong II Jean-Michel',
      'Pr. Videme Bossou Olivier',
      'MSc. Kimbi Gisèle',
    ],
    highlights: [
      'Integrated a GSM/GPRS module via UART/PPP protocol on a Raspberry Pi prototype.',
      'Documented mobile network failure patterns in Sub-Saharan Africa.',
      'Outlined a custom RIL driver adaptation pathway for AOSP-based Android TV.',
    ],
  },
]

/**
 * Current doctoral preparation. Stated as areas of study rather than
 * completed work, which is what the CV claims.
 */
export const researchInterests = {
  heading: 'Wireless Communications',
  summary:
    'In preparation for doctoral research in Computing and Communications, currently developing foundational competence across the following areas.',
  areas: [
    {
      name: 'Digital Communications and Signal Processing',
      detail: 'Modulation schemes, channel coding, OFDM, MIMO systems.',
    },
    {
      name: 'Mobile Network Protocols and Architecture',
      detail: '4G/LTE and 5G/NR standards, radio access networks, core network design.',
    },
    {
      name: 'Wireless Network Security',
      detail:
        'Physical layer security, protocol vulnerabilities, intrusion detection in wireless environments.',
    },
    {
      name: 'Network Optimisation and Resource Management',
      detail: 'QoS modelling, traffic analysis, AI-assisted network automation.',
    },
  ],
} as const

/** Competition results and distinctions. */
export const distinctions: readonly Distinction[] = [
  {
    id: 'cgweb',
    name: 'Second Prize — cgweb Startup National Coding Contest',
    year: '2024',
    detail:
      'Full-cycle development of the Driv carpooling application (Figma, Flutter, Spring Boot) with Team LAMEUTE.',
  },
  {
    id: 'camtel',
    name: 'Innovation Finalist — CAMTEL National Innovation Challenge',
    year: '2023',
    detail:
      'Silent Voice, a real-time sign language translation platform for hearing-impaired users.',
  },
]

/** Academic referees, as listed on the CV. */
export const references: readonly Reference[] = [
  {
    id: 'amougou',
    name: 'Prof. Amougou Ngoumou',
    title: 'Chief of Department, Digital Engineering',
    institution: 'UIECC-ESIGN, Sangmélima, Cameroon',
  },
  {
    id: 'bekono',
    name: 'Dr. Boris Davy Bekono',
    title: 'Lecturer and Researcher',
    institution: 'UIECC-ESIGN, Sangmélima, Cameroon',
  },
  {
    id: 'videme',
    name: 'Prof. Olivier Videme Bossou',
    title: 'Lecturer and Researcher',
    institution: 'National Advanced School of Engineering, Yaoundé',
  },
  {
    id: 'kimbi',
    name: 'Dr. Gisele Ngwanya Kimbi',
    title: 'Lecturer',
    institution: 'UIECC-ESIGN, Sangmélima, Cameroon',
  },
  {
    id: 'nguensie',
    name: 'MSc. Bernice Nguensie Wakponou',
    title: 'Lecturer (PhD student)',
    institution: 'UIECC-ESIGN, Sangmélima, Cameroon',
  },
]
